/**
 * The shell's "Not implemented in this revision" panel, as data.
 *
 * The panel exists so the signed-in screen never implies a complete product: it names
 * what the delivery plan still owes. Its only failure mode that matters is therefore
 * the inverse one, and it has happened twice — a phase that had shipped stayed listed
 * as absent, on the same page that served it. `wcaquino/saldo#54` removed the P6 line
 * once P6 landed, and the P8-T3 line outlived the merge of the bulk operations it
 * denied (card `t_f7e07804`), because both were hand-maintained strings with nothing
 * reading them.
 *
 * This module is the panel's only source, and `notImplemented.test.ts` is its guard.
 * The guard re-derives every entry's claim on every run, against three things it reads
 * from the repository:
 *
 *   1. the source tree — the phase's shipped surface (its module or route literals)
 *      must not appear in any implementation file;
 *   2. the delivery plan — the phase and at least one of its tasks must be defined
 *      there, so an entry cannot cite a phase the plan does not own;
 *   3. the acceptance register — no row whose candidate phases include this one may be
 *      `passed`, because a passed row is the repository's own record that the phase has
 *      executed.
 *
 * An entry therefore cannot outlive its feature silently: the moment the phase ships,
 * the suite fails naming the entry and the file that implements it. The list is still
 * written by hand — the repository holds no machine-readable phase-status source to
 * derive it from — so each entry names the revision it was written at, and the shell
 * renders that stamp beside the entry.
 */
export interface UnimplementedPhase {
  /** The delivery-plan phase id, e.g. `P9` (`specs/DELIVERY-PLAN.md`). */
  readonly phase: string;
  /** The panel's rendered text. */
  readonly label: string;
  /** The full commit id this entry was written at. */
  readonly verifiedAt: string;
}

/**
 * The phases this revision does not implement.
 *
 * Keep it short: an entry belongs here only while its phase is unshipped, and the
 * guard fails the suite the moment that stops being true.
 */
export const NOT_IMPLEMENTED: readonly UnimplementedPhase[] = [
  {
    phase: 'P9',
    label: 'Imports and exports (P9)',
    verifiedAt: '939176ad0b03cf5f6a2a963b35a8e694a47d33e0',
  },
];

/** The display form of the revision an entry was written at. */
export function shortRevision(commit: string): string {
  return commit.slice(0, 7);
}
